import Button from './ui/Button';
import Input, { InputControl } from './ui/Input';
import Select, { SelectControl } from './ui/Select';
import Field, { helpId } from './ui/Field';
import Segmented from './ui/Segmented';
import Checkbox from './ui/Checkbox';
import ReadonlyField from './ui/ReadonlyField';
import {
  CONSENT_KEYS,
  dayOptions,
  isMonthAllowed,
  isYearAllowed,
  PHONE_PREFIXES,
  YEARS,
} from '../helpers/registration';
import locales from '../locales.json';
import { ConsentText } from './ConstentText';

const { REGISTRATION_TEXTS, COMMON_TEXTS } = locales;

const MONTH_OPTIONS = REGISTRATION_TEXTS.months.map((label, index) => ({
  value: String(index + 1),
  label,
}));

const monthOptions = (year, day) =>
  MONTH_OPTIONS.map((month) => ({
    ...month,
    disabled: !isMonthAllowed(year, month.value, day),
  }));

const yearOptions = (month, day) =>
  YEARS.map((year) => ({
    value: year,
    label: year,
    disabled: !isYearAllowed(year, month, day),
  }));

const RegistrationForm = ({
  email,
  values,
  consents,
  errors,
  consentError,
  loading,
  submitError,
  fieldRef,
  onChange,
  onConsentChange,
  onChangeEmail,
  onSubmit,
  onBack,
}) => (
  <form className="registration" onSubmit={onSubmit} noValidate>
    <div className="sheet registration__sheet">
      <h2 className="sheet__eyebrow">{REGISTRATION_TEXTS.section}</h2>

      <ReadonlyField
        className="registration__email"
        label={REGISTRATION_TEXTS.emailLabel}
        value={email}
        action={
          <Button variant="link" disabled={loading} onClick={onChangeEmail}>
            {REGISTRATION_TEXTS.changeEmail}
          </Button>
        }
      />

      <div ref={fieldRef('salutation')}>
        <Field
          id="salutation"
          label={REGISTRATION_TEXTS.salutationLabel}
          required
          error={errors.salutation}
        >
          <Segmented
            label={REGISTRATION_TEXTS.salutationLabel}
            options={REGISTRATION_TEXTS.salutationOptions}
            value={values.salutation}
            onChange={(value) => onChange('salutation', value)}
          />
        </Field>
      </div>

      <div ref={fieldRef('firstName')}>
        <Input
          id="firstName"
          autoComplete="given-name"
          label={REGISTRATION_TEXTS.firstNameLabel}
          required
          value={values.firstName}
          error={errors.firstName}
          disabled={loading}
          onChange={(event) => onChange('firstName', event.target.value)}
        />
      </div>

      <div ref={fieldRef('lastName')}>
        <Input
          id="lastName"
          autoComplete="family-name"
          label={REGISTRATION_TEXTS.lastNameLabel}
          required
          value={values.lastName}
          error={errors.lastName}
          disabled={loading}
          onChange={(event) => onChange('lastName', event.target.value)}
        />
      </div>

      <div ref={fieldRef('phone')}>
        <Field id="phone" label={REGISTRATION_TEXTS.phoneLabel} required error={errors.phone}>
          <div className="field__group field__group--phone">
            <SelectControl
              id="phonePrefix"
              aria-label={REGISTRATION_TEXTS.phonePrefixLabel}
              options={PHONE_PREFIXES}
              value={values.phonePrefix}
              disabled={loading}
              onChange={(event) => onChange('phonePrefix', event.target.value)}
            />
            <InputControl
              id="phoneNumber"
              inputMode="tel"
              autoComplete="tel"
              aria-label={REGISTRATION_TEXTS.phoneNumberLabel}
              error={errors.phone}
              describedBy={errors.phone ? helpId('phone') : undefined}
              value={values.phone}
              disabled={loading}
              onChange={(event) => onChange('phone', event.target.value)}
            />
          </div>
        </Field>
      </div>

      <div ref={fieldRef('birthDate')}>
        <Field
          id="birthDate"
          label={REGISTRATION_TEXTS.birthDateLabel}
          required
          error={errors.birthDate}
        >
          <div className="field__group field__group--date">
            <SelectControl
              id="birthDay"
              aria-label={REGISTRATION_TEXTS.dayPlaceholder}
              placeholder={REGISTRATION_TEXTS.dayPlaceholder}
              options={dayOptions(values.birthYear, values.birthMonth)}
              error={errors.birthDate}
              value={values.birthDay}
              disabled={loading}
              onChange={(event) => onChange('birthDay', event.target.value)}
            />
            <SelectControl
              id="birthMonth"
              aria-label={REGISTRATION_TEXTS.monthPlaceholder}
              placeholder={REGISTRATION_TEXTS.monthPlaceholder}
              options={monthOptions(values.birthYear, values.birthDay)}
              error={errors.birthDate}
              value={values.birthMonth}
              disabled={loading}
              onChange={(event) => onChange('birthMonth', event.target.value)}
            />
            <SelectControl
              id="birthYear"
              aria-label={REGISTRATION_TEXTS.yearPlaceholder}
              placeholder={REGISTRATION_TEXTS.yearPlaceholder}
              options={yearOptions(values.birthMonth, values.birthDay)}
              error={errors.birthDate}
              describedBy={errors.birthDate ? helpId('birthDate') : undefined}
              value={values.birthYear}
              disabled={loading}
              onChange={(event) => onChange('birthYear', event.target.value)}
            />
          </div>
        </Field>
      </div>

      <div ref={fieldRef('street')}>
        <Input
          id="street"
          autoComplete="off"
          label={REGISTRATION_TEXTS.streetLabel}
          required
          value={values.street}
          error={errors.street}
          disabled={loading}
          onChange={(event) => onChange('street', event.target.value)}
        />
      </div>

      <div ref={fieldRef('city')}>
        <Input
          id="city"
          autoComplete="address-level2"
          label={REGISTRATION_TEXTS.cityLabel}
          required
          value={values.city}
          error={errors.city}
          disabled={loading}
          onChange={(event) => onChange('city', event.target.value)}
        />
      </div>

      <div ref={fieldRef('zip')}>
        <Input
          id="zip"
          inputMode="numeric"
          autoComplete="postal-code"
          label={REGISTRATION_TEXTS.zipLabel}
          required
          value={values.zip}
          error={errors.zip}
          disabled={loading}
          onChange={(event) => onChange('zip', event.target.value)}
        />
      </div>

      <Select
        id="country"
        autoComplete="country"
        label={REGISTRATION_TEXTS.countryLabel}
        required
        options={REGISTRATION_TEXTS.countries}
        value={values.country}
        disabled={loading}
        onChange={(event) => onChange('country', event.target.value)}
      />
    </div>

    <section className="consent" ref={fieldRef('consents')}>
      <h3 className="consent__title">{REGISTRATION_TEXTS.consentTitle}</h3>
      <p className="sheet__body">{REGISTRATION_TEXTS.consentIntro}</p>

      {CONSENT_KEYS.map((key) => (
        <Checkbox
          key={key}
          id={`consent-${key}`}
          checked={consents[key]}
          invalid={consentError && !consents[key]}
          onChange={(checked) => onConsentChange(key, checked)}
        >
          <ConsentText consent={REGISTRATION_TEXTS.consents[key]} />
        </Checkbox>
      ))}

      <p
        className={['consent__note', consentError ? 'consent__note--error' : '']
          .filter(Boolean)
          .join(' ')}
        role={consentError ? 'alert' : undefined}
      >
        {consentError ? REGISTRATION_TEXTS.consentError : REGISTRATION_TEXTS.consentNote}
      </p>
    </section>

    {submitError && (
      <p className="registration__error" role="alert">
        {submitError}
      </p>
    )}

    <div className="sheet__actions registration__actions">
      <Button variant="ghost" block disabled={loading} onClick={onBack}>
        {COMMON_TEXTS.back}
      </Button>
      <Button variant="primary" block type="submit" loading={loading}>
        {loading ? COMMON_TEXTS.registering : REGISTRATION_TEXTS.submit}
      </Button>
    </div>
  </form>
);

export default RegistrationForm;
